import React, { useState, useEffect } from 'react';

import { Field } from 'react-final-form';

import { RangeFilterLayout } from '../../../components';

import css from './IntegerRangeFilter.module.css';

const RADIX = 10;

//  Ensures that the given values object has both minimum and maximum values.
//  If `values` does not include `minValue` or `maxValue`, defaults are applied.
//  Values are clamped to [defaultMin, defaultMax] and ordered ascending —
//  URL query params arrive unvalidated (e.g. "?pub_x=50,10" or out-of-bounds
//  numbers) and RangeSlider throws on descending or out-of-range handles.
const resolveMinMaxValues = (values, defaultMax, defaultMin) => {
  const { maxValue, minValue } = values || {};
  const clamp = value =>
    Number.isFinite(value) ? Math.min(Math.max(value, defaultMin), defaultMax) : null;
  const clampedMin = clamp(minValue);
  const clampedMax = clamp(maxValue);
  const resolvedMin = clampedMin != null ? clampedMin : defaultMin;
  const resolvedMax = clampedMax != null ? clampedMax : defaultMax;
  return resolvedMin > resolvedMax
    ? { maxValue: resolvedMin, minValue: resolvedMax }
    : { maxValue: resolvedMax, minValue: resolvedMin };
};

const RangeInput = props => {
  const {
    input,
    min: defaultMinValue,
    max: defaultMaxValue,
    step,
    isInSideBar,
    initialValues,
  } = props;
  const { name, onBlur: onBlurInput, onChange, onFocus } = input;

  const currentValues = resolveMinMaxValues(
    initialValues[name],
    defaultMaxValue,
    defaultMinValue
  );

  const [fieldValues, setFieldValues] = useState(currentValues);
  const [inputValues, setInputValues] = useState({
    minValue: String(currentValues.minValue),
    maxValue: String(currentValues.maxValue),
  });

  // Re-sync local state only when the upstream values genuinely change. Depending
  // on a memoized object (whose identity flips on every parent render because
  // AllowedRangeFilter rebuilds `resolvedInitialValues` each render) would wipe
  // user typing whenever an unrelated parent state changes.
  useEffect(() => {
    setFieldValues({ minValue: currentValues.minValue, maxValue: currentValues.maxValue });
    setInputValues({
      minValue: String(currentValues.minValue),
      maxValue: String(currentValues.maxValue),
    });
  }, [currentValues.minValue, currentValues.maxValue]);

  const parseValue = value => {
    if (value === '' || value == null) {
      return null;
    }
    const parsed = Number.parseInt(value, RADIX);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const clampToBounds = value => Math.min(Math.max(value, defaultMinValue), defaultMaxValue);

  // While typing, only update the displayed string. Validation and propagation
  // happen on blur so the user can pass through intermediate invalid states
  // (e.g. deleting digits to retype the max field when min is non-zero).
  const handleMinValueChange = event => {
    const { value } = event.target;
    setInputValues(prevInputValues => ({ ...prevInputValues, minValue: value }));
  };

  const handleMaxValueChange = event => {
    const { value } = event.target;
    setInputValues(prevInputValues => ({ ...prevInputValues, maxValue: value }));
  };

  const handleBlur = blurredField => event => {
    const parsedMin = parseValue(inputValues.minValue);
    const parsedMax = parseValue(inputValues.maxValue);

    let clampedMin = parsedMin == null ? defaultMinValue : clampToBounds(parsedMin);
    let clampedMax = parsedMax == null ? defaultMaxValue : clampToBounds(parsedMax);
    if (clampedMin > clampedMax) {
      // Preserve the value the user just edited; pull the OTHER bound to match.
      if (blurredField === 'min') {
        clampedMax = clampedMin;
      } else {
        clampedMin = clampedMax;
      }
    }

    const newValues = { minValue: clampedMin, maxValue: clampedMax };
    setInputValues({
      minValue: String(clampedMin),
      maxValue: String(clampedMax),
    });
    setFieldValues(newValues);
    onChange(newValues);

    if (typeof onBlurInput === 'function') {
      onBlurInput(event);
    }
  };

  const handleSliderChange = updatedValue => {
    const newValues = { ...updatedValue };
    setFieldValues(newValues);
    setInputValues({
      minValue: String(newValues.minValue),
      maxValue: String(newValues.maxValue),
    });
    onChange(newValues);
  };

  const classes = isInSideBar ? css.formWrapper : null;

  return (
    <div className={classes}>
      <RangeFilterLayout
        min={defaultMinValue}
        max={defaultMaxValue}
        step={step}
        handles={[fieldValues.minValue, fieldValues.maxValue]}
        onSliderChange={handles => {
          handleSliderChange({ minValue: handles[0], maxValue: handles[1] });
        }}
        minInputProps={{
          placeholder: defaultMinValue,
          value: inputValues.minValue,
          onChange: handleMinValueChange,
          onBlur: handleBlur('min'),
          onFocus,
        }}
        maxInputProps={{
          placeholder: defaultMaxValue,
          value: inputValues.maxValue,
          onChange: handleMaxValueChange,
          onBlur: handleBlur('max'),
          onFocus,
        }}
      />
    </div>
  );
};

/**
 * FieldSelectIntegerRange component
 *
 * @component
 * @param {Object} props
 * @param {number} [props.min] - The minimum value
 * @param {number} [props.max] - The maximum value
 * @param {string} [props.name] - The name
 * @param {number} [props.step] - The step
 * @param {boolean} [props.isInSideBar] - Whether the filter is in the sidebar
 * @returns {JSX.Element}
 */
const FieldSelectIntegerRange = props => {
  const { max, min, name, step = 5, isInSideBar = false, ...rest } = props;
  return (
    <Field
      max={max}
      min={min}
      name={name}
      step={step}
      isInSideBar={isInSideBar}
      component={RangeInput}
      {...rest}
    />
  );
};

export default FieldSelectIntegerRange;
